import {useEffect, useRef, useState} from 'react';

import {Spinner} from './ui';
import VarInput from './VarInput';
import {C, DESCRIPTION_WIZARD_ANGLES, DESCRIPTION_WIZARD_FRAMINGS} from '../constants';
import {AI_DEFAULTS} from '../config';
import {requestJson} from '../services/ai/aiService';

// ── AI description context generation ───────────────────────────────

const SKIP_VARS = new Set(['sku', 'handle', 'status', 'vendor', 'collection', 'description', 'product_name']);

async function generateAiDescContexts(vars, brandProfile, product) {
    const category = product?.category || 'General';
    const productVars = (vars || []).filter((v) => v.sample && !SKIP_VARS.has(v.name));
    const varList = productVars.map((v) => `- {{${v.name}}} (${v.name.replace(/_/g, ' ')})`).join('\n');

    const brandContext = brandProfile ? [
        brandProfile.companyName ? `Brand: ${brandProfile.companyName}` : '',
        brandProfile.voiceStyle ? `Voice: ${brandProfile.voiceStyle}` : '',
        brandProfile.targetAudience ? `Audience: ${brandProfile.targetAudience}` : '',
    ].filter(Boolean).join('\n') : '';

    const prompt = `You are an e-commerce content strategist. Generate 6 different description context directions for a "${category}" product listing.

IMPORTANT: This product is a "${category}" — ONLY generate directions relevant to ${category}. Do NOT reference other product categories.

${brandContext}

Available template variables for this product:
${varList || 'None'}

Each context is a 1-2 sentence instruction telling the AI how to write the description.
These are reused across multiple variants — use {{variable}} formulas for product-specific data, never literal values.

Return ONLY a JSON array, no markdown:
[{"label": "short name", "context": "instruction using {{variables}}"}]

Cover different angles relevant to ${category}: material-led, benefit-led, lifestyle, technical, audience-focused, premium positioning.`;

    return requestJson({
        prompt,
        model: AI_DEFAULTS.model,
        maxTokens: 1200,
        temperature: 0.5,
    });
}

// ── Component ───────────────────────────────────────────────────────

export default function DescriptionPromptWizard({onFill, onClose, vars, brandProfile, product}) {
    const [mode, setMode] = useState('ai');
    const [manualStep, setManualStep] = useState(1);
    const [angle, setAngle] = useState(null);
    const [framing, setFraming] = useState(null);
    const [detail, setDetail] = useState('');
    const [aiLoading, setAiLoading] = useState(false);
    const [aiResults, setAiResults] = useState(null);
    const [aiError, setAiError] = useState('');
    const aiTriggered = useRef(false);

    const fillManual = () => {
        const context = [
            angle?.prompt,
            framing?.prompt,
            detail.trim() ? `Include ${detail.trim()}.` : '',
        ].filter(Boolean).join(' ');
        onFill(context);
        onClose();
    };

    const runAi = async () => {
        setAiLoading(true);
        setAiError('');
        setAiResults(null);
        try {
            const results = await generateAiDescContexts(vars, brandProfile, product);
            setAiResults(results);
        } catch (err) {
            setAiError(err.message || 'Failed to generate');
        } finally {
            setAiLoading(false);
        }
    };

    useEffect(() => {
        if (mode === 'ai' && !aiResults && !aiLoading && !aiTriggered.current) {
            aiTriggered.current = true;
            runAi();
        }
    }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            overflow: 'hidden',
            animation: 'popIn .15s ease both',
            boxShadow: `0 2px 12px ${C.shadow}`,
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '7px 10px',
                background: C.surfaceAlt,
                borderBottom: `1px solid ${C.border}`,
            }}>
                <div style={{display: 'flex', gap: 3}}>
                    {[
                        {key: 'manual', label: 'Manual'},
                        {key: 'ai', label: 'AI Suggest'},
                    ].map((m) => (
                        <button
                            key={m.key}
                            onClick={() => setMode(m.key)}
                            style={{
                                fontFamily: 'inherit',
                                fontSize: 10,
                                fontWeight: 600,
                                padding: '3px 8px',
                                borderRadius: 5,
                                border: mode === m.key ? `1px solid ${C.teal}44` : '1px solid transparent',
                                background: mode === m.key ? `${C.teal}12` : 'transparent',
                                color: mode === m.key ? C.teal : C.textMuted,
                                cursor: 'pointer',
                            }}>
                            {m.label}
                        </button>
                    ))}
                </div>
                <button
                    onClick={onClose}
                    style={{border: 'none', background: 'transparent', color: C.textFaint, cursor: 'pointer', fontSize: 14, padding: '0 2px'}}>
                    ×
                </button>
            </div>

            <div style={{padding: '10px 12px'}}>
                {/* ── AI Suggest ── */}
                {mode === 'ai' && (
                    <div>
                        {aiLoading && (
                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '20px 0', color: C.textMuted, fontSize: 12}}>
                                <Spinner /> Generating suggestions...
                            </div>
                        )}

                        {!aiResults && !aiLoading && aiError && (
                            <div>
                                <div style={{fontSize: 11, color: '#D85A30', marginBottom: 8}}>{aiError}</div>
                                <button
                                    onClick={() => { aiTriggered.current = false; runAi(); }}
                                    style={{
                                        fontFamily: 'inherit', fontSize: 11, fontWeight: 600,
                                        padding: '7px 14px', borderRadius: 7, border: 'none',
                                        background: '#1D9E75', color: '#fff', cursor: 'pointer',
                                    }}>
                                    Retry
                                </button>
                            </div>
                        )}

                        {aiResults && (
                            <>
                                <div style={{display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 280, overflowY: 'auto'}}>
                                    {aiResults.map((r, i) => (
                                        <button
                                            key={i}
                                            onClick={() => { onFill(r.context); onClose(); }}
                                            style={{
                                                fontFamily: 'inherit', textAlign: 'left',
                                                padding: '8px 10px', borderRadius: 8,
                                                border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer',
                                            }}>
                                            <span style={{fontSize: 11, fontWeight: 600, color: C.ink, display: 'block'}}>{r.label}</span>
                                            <span style={{fontSize: 10, color: C.textMuted, lineHeight: 1.4}}>{r.context}</span>
                                        </button>
                                    ))}
                                </div>
                                <button
                                    onClick={() => { setAiResults(null); aiTriggered.current = false; runAi(); }}
                                    style={{
                                        fontFamily: 'inherit', fontSize: 10, fontWeight: 600, marginTop: 8,
                                        padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.border}`,
                                        background: 'transparent', color: C.textMuted, cursor: 'pointer',
                                    }}>
                                    Regenerate
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* ── Manual mode ── */}
                {mode === 'manual' && (
                    <>
                        <div style={{display: 'flex', gap: 6, marginBottom: 10}}>
                            {[{n: 1, label: 'Focus'}, {n: 2, label: 'Framing'}, {n: 3, label: 'Detail'}].map(({n, label}) => (
                                <button
                                    key={n}
                                    onClick={() => n <= manualStep && setManualStep(n)}
                                    style={{
                                        fontFamily: 'inherit', fontSize: 10, fontWeight: 600,
                                        padding: '3px 8px', borderRadius: 5, border: 'none',
                                        background: manualStep === n ? `${C.teal}14` : 'transparent',
                                        color: manualStep === n ? C.teal : n < manualStep ? C.textMuted : C.textFaint,
                                        cursor: n <= manualStep ? 'pointer' : 'default',
                                    }}>
                                    {n < manualStep ? '✓ ' : `${n}. `}{label}
                                </button>
                            ))}
                        </div>

                        {manualStep === 1 && (
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5}}>
                                {DESCRIPTION_WIZARD_ANGLES.map((a) => (
                                    <button
                                        key={a.label}
                                        onClick={() => { setAngle(a); setManualStep(2); }}
                                        style={{
                                            fontFamily: 'inherit', fontSize: 11, fontWeight: 500,
                                            padding: '7px 10px', borderRadius: 8, textAlign: 'left',
                                            border: `1px solid ${angle?.label === a.label ? `${C.coral}66` : C.border}`,
                                            background: angle?.label === a.label ? `${C.coral}10` : 'transparent',
                                            color: C.ink, cursor: 'pointer',
                                        }}>
                                        {a.label}
                                        <span style={{display: 'block', fontSize: 9.5, color: C.textMuted, marginTop: 1}}>{a.hint}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {manualStep === 2 && (
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5}}>
                                {DESCRIPTION_WIZARD_FRAMINGS.map((f) => (
                                    <button
                                        key={f.label}
                                        onClick={() => { setFraming(f); setManualStep(3); }}
                                        style={{
                                            fontFamily: 'inherit', fontSize: 11, fontWeight: 500,
                                            padding: '7px 10px', borderRadius: 8, textAlign: 'left',
                                            border: `1px solid ${framing?.label === f.label ? `${C.amber}66` : C.border}`,
                                            background: framing?.label === f.label ? `${C.amber}10` : 'transparent',
                                            color: C.ink, cursor: 'pointer',
                                        }}>
                                        {f.label}
                                        <span style={{display: 'block', fontSize: 9.5, color: C.textMuted, marginTop: 1}}>{f.eg}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {manualStep === 3 && (
                            <>
                                <div style={{fontSize: 11, color: C.textMuted, marginBottom: 8}}>
                                    Add a concrete detail or variable to ground the copy.
                                </div>
                                <VarInput
                                    value={detail}
                                    onChange={setDetail}
                                    placeholder="e.g. {{finish}} finish, ideal for boutique interiors"
                                    vars={vars}
                                    style={{borderColor: C.border, background: C.surfaceAlt}}
                                />
                                <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10}}>
                                    <button
                                        onClick={() => setManualStep(2)}
                                        style={{fontFamily: 'inherit', fontSize: 11, padding: '6px 12px', borderRadius: 7, border: `1px solid ${C.border}`, background: 'transparent', color: C.textMuted, cursor: 'pointer'}}>
                                        Back
                                    </button>
                                    <button
                                        onClick={fillManual}
                                        disabled={!detail.trim()}
                                        style={{fontFamily: 'inherit', fontSize: 11, fontWeight: 600, padding: '6px 12px', borderRadius: 7, border: 'none', background: detail.trim() ? '#1D9E75' : C.surfaceAlt, color: detail.trim() ? '#fff' : C.textMuted, cursor: detail.trim() ? 'pointer' : 'not-allowed'}}>
                                        Fill context
                                    </button>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
